class HTMLUpdateUtility {
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input-dawn', QuantityInputDawn);

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const productInfo = this.closest('product-info');
      const isCombinedListingParent = productInfo?.dataset.combinedListingParent === "true";
      const target = this.getInputForEventTarget(event.target);
      
      if (isCombinedListingParent) {
        const productUrl = target?.dataset.productUrl?.trim();
        
        if (productUrl && productUrl !== productInfo?.dataset.url && productUrl !== '') {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          window.location.replace(productUrl);
          return;
        }
      }
      
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {

      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');

      const newSelectedOption = Array.from(target.options).find((option) => option.value === value);
      if (newSelectedOption) {
        newSelectedOption.setAttribute('selected', 'selected');
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .product-option__swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('product-option__swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('product-option__swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}
customElements.define('product-variant-selects', ProductVariantSelects);

if (!customElements.get('product-info')) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        this.productMediaGallery = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        this.productVariantSelects = this.querySelector('product-variant-selects');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );
        this.initUpdateMedia();

        if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
          this.updateVariantMedia();
        }

        if (this.dataset.combinedListingParent === "true" && window.Shopify?.designMode) {
          console.log('[Combined Listings] Parent product detected:', this.dataset.url);
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(
          ".product-form__quantity"
        );
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initUpdateMedia() {
        const variantIdFromUrl = parseInt(window.location.search.split('?').filter(item => item.includes('variant='))[0]?.split('=')[1]) || null;
        if (!variantIdFromUrl) { return; }
        const variant = this.getSelectedVariant(this);
        if (!variant || !variantIdFromUrl) { return; }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push(html =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach(element =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push(newNode => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues }
      }) {
        if (!this.contains(event.target)) return;

        const isCombinedListingParent = this.dataset.combinedListingParent === "true";
        
        if (isCombinedListingParent) {
          let inputElement = target;
          if (target.tagName !== 'INPUT' && target.tagName !== 'OPTION') {
            const labelFor = target.getAttribute('for');
            if (labelFor) {
              inputElement = document.getElementById(labelFor);
            } else {
              inputElement = target.querySelector('input[type="radio"]:checked') || 
                           target.closest('fieldset')?.querySelector('input[type="radio"]:checked');
            }
          }
          
          const productUrl = inputElement?.dataset.productUrl?.trim() || 
            target.dataset.productUrl?.trim() ||
            this.pendingRequestUrl?.trim() ||
            this.dataset.url;
          
          if (productUrl && productUrl !== this.dataset.url && productUrl !== '') {
            if (window.Shopify?.designMode) {
              console.log('[Combined Listings] Navigation to child product:', productUrl);
            }
            if (event && event.preventDefault) {
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
            }
            this.abortController?.abort();
            this.abortController = new AbortController();
            window.location.replace(productUrl);
            return;
          }
        }

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;

        this.resetProductFormState();
        this.pendingRequestUrl = productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl)
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return html => {
          const isCombinedListingParent = this.dataset.combinedListingParent === "true";
          
          if (isCombinedListingParent && productUrl && productUrl !== this.dataset.url) {
            return;
          }

          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(
            html.querySelector(selector)
          );
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            const newMain = html.querySelector("main");
            const clonedMain = newMain.cloneNode(true);
            clonedMain.querySelectorAll('script').forEach(script => script.remove());
            
            const currentMain = document.querySelector("main");
            currentMain.parentNode.replaceChild(clonedMain, currentMain);
            
            const thumbsSwiper = document.querySelector('main swiper-product-thumbs');
            const gallerySwiper = document.querySelector('main swiper-product-gallery');
            
            requestAnimationFrame(() => {
              if (thumbsSwiper && typeof thumbsSwiper.init === 'function') {
                try {
                  thumbsSwiper.init();
                } catch (e) {
                  console.error('Error initializing thumbs Swiper:', e);
                }
              }
              
              if (gallerySwiper && typeof gallerySwiper.init === 'function') {
                const correctWidth = gallerySwiper.clientWidth;
                
                const swiperContainer = gallerySwiper.querySelector('.swiper');
                
                if (swiperContainer) {
                  swiperContainer.style.maxWidth = '100%';
                  swiperContainer.style.width = '100%';
                }
                
                try {
                  gallerySwiper.init();
                  
                  if (gallerySwiper.instance) {
                    if (gallerySwiper.clientWidth > correctWidth * 1.5) {
                      gallerySwiper.style.width = correctWidth + 'px';
                      if (swiperContainer) {
                        swiperContainer.style.width = correctWidth + 'px';
                      }
                      
                      if (typeof gallerySwiper.instance.update === 'function') {
                        gallerySwiper.instance.update();
                      }
                    }
                  }
                } catch (e) {
                  console.error('Error initializing gallery Swiper:', e);
                }
              }
            });
            
            if (variant) {
              this.reloadProductSections(productUrl, variant, html);
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            
            if (variant) {
              this.reloadProductSections(productUrl, variant);
            }
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then(response => response.text())
          .then(responseText => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch(error => {
            if (error.name === "AbortError") {
            } else {
              // console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "product-variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage &&
          params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const productVariantSelects = html.querySelector("product-variant-selects");
        if (productVariantSelects) {
          const measmerizeBtn = document.getElementById('measmerize-button');
          if (measmerizeBtn?.parentNode) {
            measmerizeBtn.parentNode.removeChild(measmerizeBtn);
          }
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            productVariantSelects,
            this.preProcessHtmlCallbacks
          );
          const newVariantSelectors = this.variantSelectors?.previousElementSibling;
          const slot = document.getElementById('product__find-size-slot');
          if (measmerizeBtn && slot) {
            slot.appendChild(measmerizeBtn);
          }
          const sizeOption = newVariantSelectors?.querySelector?.('#size-option') ?? document.querySelector('#size-option');
          const sizeSelectedValue = sizeOption?.querySelector?.('[data-selected-value]');
          const checkedSizeInput = sizeOption?.querySelector?.('.product__size-variant-input:checked');
          if (sizeSelectedValue && checkedSizeInput?.value) {
            sizeSelectedValue.innerHTML = checkedSizeInput.value;
          }
        }
      }

      handleUpdateProductInfo(productUrl) {
        return html => {
          const variant = this.getSelectedVariant(html);

          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateVariantDiscountBadge(variant?.price, variant?.compare_at_price);
          this.updateVariantDetails(variant?.id);
          if (this.pickupAvailability && variant?.id) {
            this.pickupAvailability.dataset.variantId = variant.id;
            this.pickupAvailability.fetchAvailability(variant.id);
          }

          if (this.productMediaGallery && this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true') {
            this.updateVariantMedia(html);
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = source => false
          ) => {
            const sourceId = `${id}-${this.sectionId}`;
            const destId = `#${id}-${this.dataset.section}`;
            const source = html.getElementById(sourceId);
            const destination = this.querySelector(destId);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;

              if (id === 'Inventory') {
                const inventoryThreshold = parseInt(source.dataset.inventoryThreshold);
                const inventoryQuantity = parseInt(source.dataset.inventoryQuantity);
                destination.classList.toggle("hidden", inventoryThreshold && inventoryQuantity > inventoryThreshold || inventoryQuantity < 1);
              } else {
                destination.classList.toggle("hidden", shouldHide(source));
              }
            }
          };

          updateSourceFromDestination("ProductPurchaseInfo");
          updateSourceFromDestination("ProductStickyCart");

          updateSourceFromDestination("price");
          this.querySelector(`#price-${this.dataset.section}`)?.classList.remove("hidden");
          updateSourceFromDestination("Sku", ({ classList }) => classList.contains("hidden"));
          
          this.updateSKU(variant);

          updateSourceFromDestination("Inventory", ({ innerText }) => innerText === "");

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut
          );

          this.reloadProductSections(productUrl, variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant
            }
          });
        };
      }

      async reloadProductSections(productUrl, variant, htmlDoc = null) {
        const sectionsToReload = [
          'section-product-material-description',
          'section-product-performance',
          'section-product-model-description',
          'section-product-garment-care',
          'section-shop-the-look',
          'section-features-grid'
        ];

        try {
          let main = null;
          
          if (htmlDoc) {
            main = htmlDoc.querySelector('main');
          } else {
            const parentProductUrl = productUrl.split('?')[0];  
            const response = await fetch(parentProductUrl);
            if (!response.ok) return;
            
            const htmlText = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlText, 'text/html');
            main = doc.querySelector('main');
          }
          
          if (!main) return;
          
          const fixFadeInAnimations = (container = document) => {
            const allFadeInElements = container.querySelectorAll('.js-animation-fade-in');
            allFadeInElements.forEach(el => {
              el.classList.add('animation-none');
              el.classList.remove('js-animation-fade-in');
              el.style.opacity = '1';
            });
          };

          const variantSku = variant?.sku;
          
          sectionsToReload.forEach(sectionClass => {
            const newSection = main.querySelector(`section.${sectionClass}, section[class*="${sectionClass}"]`);
            const currentSection = document.querySelector(`section.${sectionClass}, section[class*="${sectionClass}"]`);
            
            if (newSection && currentSection) {
              const clonedNewSection = newSection.cloneNode(true);
              
              clonedNewSection.querySelectorAll('script').forEach(script => script.remove());
              
              currentSection.innerHTML = clonedNewSection.innerHTML;
              
              const sectionHasFadeIn = currentSection.classList.contains('js-animation-fade-in');
              
              if (sectionHasFadeIn) {
                currentSection.classList.add('animation-none');
                currentSection.classList.remove('js-animation-fade-in');
                currentSection.style.opacity = '1';
              }
              
              fixFadeInAnimations(currentSection);
              
              currentSection.querySelectorAll('img').forEach(img => {
                if (img.dataset.src) {
                  img.src = img.dataset.src;
                  delete img.dataset.src;
                }
                
                if (img.getAttribute('loading') === 'lazy') {
                  img.removeAttribute('loading');
                  void img.offsetHeight;
                }
              });
              
              if (window.lazyload && typeof window.lazyload.update === 'function') {
                window.lazyload.update();
              } else if (window.lazySizes && typeof window.lazySizes.autoSizer === 'object') {
                window.lazySizes.autoSizer.checkElems();
              }
              
              currentSection.querySelectorAll('swiper-product-gallery, swiper-product-thumbs').forEach(swiperEl => {
                try {
                  if (swiperEl.instance && typeof swiperEl.instance.update === 'function') {
                    swiperEl.instance.update();
                  } else if (swiperEl.swiper && typeof swiperEl.swiper.update === 'function') {
                    swiperEl.swiper.update();
                  } else if (typeof swiperEl.init === 'function') {
                    swiperEl.init();
                  }
                } catch (e) {
                  console.error('Error reinitializing Swiper:', e);
                }
              });

              requestAnimationFrame(() => {
                currentSection.querySelectorAll('swiper-component').forEach(comp => {
                  try {
                    if (typeof comp.init === 'function') {
                      comp.init();
                    }
                  } catch (e) {
                    console.error('Error reinitializing swiper-component:', e);
                  }
                });
                if (sectionClass === 'section-product-model-description' && typeof initModelDescriptionCarouselDots === 'function') {
                  initModelDescriptionCarouselDots(currentSection);
                }
              });

              currentSection.querySelectorAll('shop-the-look-slider').forEach(el => {
                requestAnimationFrame(() => {
                  if (typeof el.initSlider === 'function') {
                    try {
                      el.initSlider();
                    } catch (e) {
                      console.error('Error reinitializing shop-the-look-slider:', e);
                    }
                  }
                });
              });
              
              const feedatyWidgets = currentSection.querySelectorAll('.feedaty_widget');
              feedatyWidgets.forEach(widget => {
                if (!widget.querySelector('.fdt_merchant300x100-2_widget_merchant')) {
                  const existingScript = document.querySelector('script.feedaty_sdk');
                  if (!existingScript) {
                    const script = document.createElement('script');
                    script.className = 'feedaty_sdk';
                    script.src = 'https://widget.feedaty.com/v3.0.0/js/2021/10214094/feedaty.min.js';
                    script.async = true;
                    script.onload = () => {
                      setTimeout(() => {
                        if (typeof window.styleFeedatyWidget === 'function') {
                          window.styleFeedatyWidget();
                        }
                      }, 500);
                    };
                    document.head.appendChild(script);
                  } else {
                    const tempDiv = document.createElement('div');
                    widget.appendChild(tempDiv);
                    widget.removeChild(tempDiv);
                    
                    const event = new CustomEvent('feedaty:reload', { bubbles: true });
                    widget.dispatchEvent(event);
                    
                    setTimeout(() => {
                      if (typeof window.styleFeedatyWidget === 'function') {
                        window.styleFeedatyWidget();
                      }
                    }, 500);
                  }
                }
              });
            } else if (newSection && !currentSection) {
              const mainElement = document.querySelector('main');
              if (mainElement) {
                const productSection = mainElement.querySelector('section.main-product, section[id*="main"], section[id*="MainProduct"]');
                if (productSection) {
                  const clonedSection = newSection.cloneNode(true);
                  if (productSection.nextElementSibling) {
                    productSection.parentNode.insertBefore(clonedSection, productSection.nextElementSibling);
                  } else {
                    productSection.parentNode.appendChild(clonedSection);
                  }
                  
                  clonedSection.querySelectorAll('swiper-product-gallery, swiper-product-thumbs').forEach(swiperEl => {
                    if (typeof swiperEl.init === 'function') {
                      swiperEl.init();
                    }
                  });
                }
              }
            }
          });
          
          requestAnimationFrame(() => {
            setTimeout(() => {
              fixFadeInAnimations();
              
              const allFeedatyWidgets = document.querySelectorAll('.feedaty_widget');
              const emptyWidgets = Array.from(allFeedatyWidgets).filter(widget => 
                !widget.querySelector('.fdt_merchant300x100-2_widget_merchant')
              );
              
              if (emptyWidgets.length > 0) {
                const existingScript = document.querySelector('script.feedaty_sdk');
                if (!existingScript) {
                  const script = document.createElement('script');
                  script.className = 'feedaty_sdk';
                  script.src = 'https://widget.feedaty.com/v3.0.0/js/2021/10214094/feedaty.min.js';
                  script.async = true;
                  script.onload = () => {
                    setTimeout(() => {
                      if (typeof window.styleFeedatyWidget === 'function') {
                        window.styleFeedatyWidget();
                      }
                    }, 1000);
                  };
                  document.head.appendChild(script);
                } else {
                  emptyWidgets.forEach(widget => {
                    const tempDiv = document.createElement('div');
                    widget.appendChild(tempDiv);
                    widget.removeChild(tempDiv);
                    
                    const event = new CustomEvent('feedaty:reload', { bubbles: true });
                    widget.dispatchEvent(event);
                  });
                  
                  setTimeout(() => {
                    if (typeof window.styleFeedatyWidget === 'function') {
                      window.styleFeedatyWidget();
                      setTimeout(() => window.styleFeedatyWidget(), 500);
                      setTimeout(() => window.styleFeedatyWidget(), 1000);
                      setTimeout(() => window.styleFeedatyWidget(), 2000);
                    }
                  }, 500);
                }
              }
            }, 100);
          });
        } catch (error) {
          console.error('Error reloading product sections:', error);
        }
      }

      updateVariantMedia() {
        const select_variant_options_handle = this.querySelector(
          'product-variant-selects [data-selected-variant-options-handle]'
        )?.innerHTML.trim();

        if (!select_variant_options_handle) {
          const productAllVariants = this.productVariantSelects.querySelector('[data-all-variants]').innerHTML.trim();

          this.productVariantSelects.addEventListener('click', (event) => {
            const closestVariantOptionInput = event.target.closest('.product-option__input');

            if (closestVariantOptionInput) {
              const closestVariantOptionInputValue = closestVariantOptionInput.value;

              const availableVariant = JSON.parse(productAllVariants).find(variant => {
                return variant.options.some(option => option === closestVariantOptionInputValue);
              });
            } else {
              // console.log('No variant option input found');
            }
          });
        }

        this.productMediaGallery.querySelectorAll('[data-media-group]').forEach(element => element.classList.add('hidden'));

        this.productMediaGallery.querySelectorAll(`[data-media-group="${select_variant_options_handle}"]`).forEach(element => element.classList.remove('hidden'));

        this.productMediaGallery.querySelectorAll('swiper-product-gallery').forEach(swiperSlide => {
          if (swiperSlide.instance) {
            swiperSlide.instance.update(); 
          } else {
            swiperSlide.init(); 
          }
        });
      }

      updateVariantOptionMedia() {
        this.productMediaGallery.querySelectorAll('[data-media-group]:not(.hidden)').forEach(element => {

        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach(productForm => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${
            variantId ? `?variant=${variantId}` : ""
          }`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return;
        if (!sellingPlanId) {
          window.history.replaceState({}, "", `${window.location.href.replace(/&selling_plan=\d+/, "")}`);
        } else {
          const params = new URLSearchParams(window.location.search);
          params[params.has("variant") ? "set" : "append"](
            "variant",
            selectedVariantId
          );
          if (sellingPlanId) {
            params[params.has("selling_plan") ? "set" : "append"](
              "selling_plan",
              sellingPlanId
            );
          } else {
            params.delete("selling_plan");
          }
          window.history.replaceState({}, "", `${this.dataset.url}?${params.toString()}`);
        }
      }

      updateSKU(variant) {
        const modelDescriptionSkuValue = document.querySelector('.section-product-model-description__sku-value');
        if (modelDescriptionSkuValue) {
          if (variant?.sku && variant.sku !== '') {
            modelDescriptionSkuValue.textContent = variant.sku;
            const skuWrapper = modelDescriptionSkuValue.closest('.section-product-model-description__sku-wrapper');
            if (skuWrapper) {
              skuWrapper.style.display = 'block';
            }
          } else {
            const skuWrapper = modelDescriptionSkuValue.closest('.section-product-model-description__sku-wrapper');
            if (skuWrapper) {
              skuWrapper.style.display = 'none';
            }
          }
        }
        
        let skuElement = this.querySelector(`#Sku-${this.dataset.section}`);
        
        if (!skuElement) {
          skuElement = document.querySelector(`#Sku-${this.dataset.section}`);
        }
        
        if (!skuElement) {
          const skuByClass = this.querySelector('.product__sku');
          if (skuByClass && skuByClass.id && skuByClass.id.includes('Sku')) {
            skuElement = skuByClass;
          }
        }
        
        if (!skuElement) {
          const allSkuElements = document.querySelectorAll('.product__sku, [id*="Sku"]');
          if (allSkuElements.length > 0) {
            for (const el of allSkuElements) {
              if (el.id && el.id.includes(this.dataset.section)) {
                skuElement = el;
                break;
              }
            }
            if (!skuElement && allSkuElements.length > 0) {
              skuElement = allSkuElements[0];
            }
          }
        }
        
        if (skuElement) {
          const skuSpan = skuElement.querySelector('span');
          
          if (skuSpan) {
            if (variant?.sku && variant.sku !== '') {
              skuSpan.textContent = '#' + variant.sku;
              skuElement.classList.remove('hidden');
              skuElement.style.display = '';
            } else {
              skuElement.classList.add('hidden');
            }
          } else {
            if (variant?.sku && variant.sku !== '') {
              skuElement.textContent = '#' + variant.sku;
              skuElement.classList.remove('hidden');
              skuElement.style.display = '';
            }
          }
        } else {
          if (variant?.sku && variant.sku !== '') {
            const productInfoBlocks = this.querySelector('.product__info-blocks, .product__text, [class*="product__info"]');
            const productInfo = this.querySelector('product-info');
            
            if (productInfoBlocks || productInfo) {
              const container = productInfoBlocks || productInfo;
              const skuDiv = document.createElement('div');
              skuDiv.id = `Sku-${this.dataset.section}`;
              skuDiv.className = 'product__sku';
              skuDiv.setAttribute('role', 'status');
              skuDiv.innerHTML = `<span>#${variant.sku}</span>`;
              
              const priceElement = container.querySelector('#price-' + this.dataset.section);
              if (priceElement && priceElement.parentNode) {
                priceElement.parentNode.insertBefore(skuDiv, priceElement.nextSibling);
              } else {
                container.appendChild(skuDiv);
              }
            }
          }
        }
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll('.product__metafield_for_variant-block');
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute('data-block-id');
            const metafieldDataElement = this.querySelector(`#product__metafield_for_variant-block--${block_id}`);
            if (!metafieldDataElement) {
              return;
            }
            try {
              let productVariantDetailsData = JSON.parse(metafieldDataElement?.textContent);
              productVariantDetail.innerHTML = productVariantDetailsData?.[variantId]?.replaceAll('\n', '<br />') || '';
            } catch (error) {
              console.error('Error parsing JSON', error);
            }
          });
        }
      }

      updateVariantDiscountBadge(price, compare_at_price) {
        const discountBadge = this.querySelector(".product-card__badge--discount");
        if (!discountBadge) return;


        if (compare_at_price > price) {
          discountBadge.classList.remove("hidden");
          const percentageElement = discountBadge.querySelector(".percentage");
          const discountPercentage = Math.round(
            ((compare_at_price - price) / compare_at_price) * 100
          );
          percentageElement.textContent = discountPercentage;
        } else {
          discountBadge.classList.add("hidden");
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Quantity-Rules"
        ]
          .map(id => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const gridLayout = this.querySelector('.main-product__media--grid');
        if (gridLayout) {
          gridLayout.querySelectorAll('.swiper-slide').forEach(item => {
            item.classList.remove('active');
          });
          const activeSlide = gridLayout.querySelector(`.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`);
          if (activeSlide) {
            activeSlide.classList.add('active');
          }
        }

        if (
          this.querySelector(".main-product__media")?.hasAttribute(
            "data-desktop-grid"
          ) && window.innerWidth >= 750
        ) {
          this.scrollToImage(variantFeaturedMediaId);
        }

        const productMedia = this.querySelector(
          "swiper-product-gallery"
        );
        productMedia?.setActiveMedia(variantFeaturedMediaId);
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll("[data-media-id]").forEach(img => {
          if (
            img.getAttribute("data-media-id") ==
            variantFeaturedMediaId
          ) {
            img.scrollIntoView({
              block: "end",
              behavior: "smooth"
            });
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step
            ? parseInt(this.quantityInput.step)
            : 1
        };

        let min = data.min;
        const max =
          data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min)
          min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then(response => response.text())
          .then(responseText => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch(e => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label"
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step"
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("product-variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener(
          "submit",
          this.onSubmitHandler.bind(this)
        );
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText =
          this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (
          this.submitButton.getAttribute("aria-disabled") === "true"
        )
          return;

        const recipientForm = this.querySelector('recipient-form');
        if (recipientForm) {
          const checkbox = recipientForm.querySelector('input[type="checkbox"]');
          if (checkbox && checkbox.checked) {
            const emailInput = recipientForm.querySelector('input[type="email"]');
            const emailValue = emailInput ? emailInput.value.trim() : '';
            
            if (!emailValue) {
              const errorWrapper = recipientForm.querySelector('.product-form__recipient-error-message-wrapper');
              const emailError = recipientForm.querySelector('#RecipientForm-email-error-' + recipientForm.dataset.sectionId);
              
              if (errorWrapper) {
                errorWrapper.hidden = false;
              }
              
              if (emailError) {
                emailError.classList.remove('hidden');
                const errorText = emailError.querySelector('.error-message');
                if (errorText) {
                  const errorMessage = recipientForm.dataset.emailRequiredError || 
                                      'Email is required when sending as a gift.';
                  errorText.textContent = errorMessage;
                }
              }
              
              if (emailInput) {
                emailInput.setAttribute('aria-invalid', 'true');
                emailInput.focus();
              }
              
              this.submitButton.removeAttribute("aria-disabled");
              this.submitButton.classList.remove("loading");
              const spinner = this.querySelector(".loading__spinner");
              if (spinner) {
                spinner.classList.add("hidden");
              }
              
              return;
            }
            
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(emailValue)) {
              const errorWrapper = recipientForm.querySelector('.product-form__recipient-error-message-wrapper');
              const emailError = recipientForm.querySelector('#RecipientForm-email-error-' + recipientForm.dataset.sectionId);
              
              if (errorWrapper) {
                errorWrapper.hidden = false;
              }
              
              if (emailError) {
                emailError.classList.remove('hidden');
                const errorText = emailError.querySelector('.error-message');
                if (errorText) {
                  errorText.textContent = 'Please enter a valid email address.';
                }
              }
              
              if (emailInput) {
                emailInput.setAttribute('aria-invalid', 'true');
                emailInput.focus();
              }
              
              this.submitButton.removeAttribute("aria-disabled");
              this.submitButton.classList.remove("loading");
              const spinner = this.querySelector(".loading__spinner");
              if (spinner) {
                spinner.classList.add("hidden");
              }
              
              return;
            }
          }
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove(
          "hidden"
        );

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        const variantId = formData.get("id");
        
        const newProperties = {};
        
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[') && key.endsWith(']')) {
            const propertyName = key.slice(11, -1); 
            if (propertyName && value && !propertyName.startsWith('_') && propertyName !== 'Send as gift') {
              newProperties[propertyName] = value;
            }
          }
        }
        
        const personalizationDrawer = document.querySelector('personalization-drawer');
        const giftPackagingDrawer = document.querySelector('gift-packaging-drawer');
        
        if (personalizationDrawer && personalizationDrawer.hasPersonalization()) {
          const personalizationData = personalizationDrawer.getPersonalizationData();
          
          if (personalizationData.initials) {
            newProperties['Iniziali'] = personalizationData.initials;
          }
          
          if (personalizationData.font) {
            const fontLabel = personalizationData.font === 'standard' ? 'Standard' : 'Corsivo';
            newProperties['Carattere'] = fontLabel;
          }
          
          if (personalizationData.color) {
            const colorLabels = {
              'white': 'Bianco',
              'dark-blue': 'Blu scuro',
              'light-blue': 'Blu chiaro',
              'dark-gray': 'Grigio scuro',
              'light-pink': 'Rosa chiaro',
              'light-purple': 'Viola chiaro',
              'red': 'Rosso',
              'black': 'Nero',
              'light-gray': 'Grigio chiaro',
              'brown': 'Marrone',
              'dark-green': 'Verde scuro'
            };
            const colorLabel = colorLabels[personalizationData.color] || personalizationData.color;
            newProperties['Colore ricamo'] = colorLabel;
          }
          
          if (personalizationData.position) {
            const positionLabel = personalizationData.position === 'cuff' ? 'Polso' : 'Davanti';
            newProperties['Posizione'] = positionLabel;
          }
          
          newProperties['Customized'] = 'Yes';
          newProperties['rever_non_returnable'] = 'true';
        }
        
        if (giftPackagingDrawer && giftPackagingDrawer.hasGiftPackaging()) {
          const giftPackagingData = giftPackagingDrawer.getGiftPackagingData();
          
          if (giftPackagingData.recipient) {
            newProperties['Questo regalo è per'] = giftPackagingData.recipient;
          }
          
          if (giftPackagingData.message) {
            newProperties['Messaggio'] = giftPackagingData.message;
          }
          
          if (giftPackagingData.hidePrice) {
            newProperties['Nascondi prezzo'] = 'Sì';
          }
        }
        
        let existingItem = null;
        let mergedQuantity = parseInt(formData.get("quantity") || 1);
        
        try {
          let cartUrl = (typeof routes !== 'undefined' && routes.cart_url) || (window.routes?.cart_url) || '/cart.js';
          if (cartUrl && !cartUrl.endsWith('.js')) {
            cartUrl = cartUrl + '.js';
          }
          
          const cartResponse = await fetch(cartUrl);
          
          if (!cartResponse.ok) {
            throw new Error(`Cart fetch failed: ${cartResponse.status}`);
          }
          
          const contentType = cartResponse.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            const text = await cartResponse.text();
            console.warn('Cart API returned non-JSON response:', text.substring(0, 100));
            throw new Error('Cart API returned non-JSON response');
          }
          
          const cartData = await cartResponse.json();
          
          existingItem = cartData.items?.find(item => item.variant_id === parseInt(variantId));
          
          if (existingItem) {
            if (existingItem.properties) {
              existingItem.properties.forEach(prop => {
                if (!newProperties.hasOwnProperty(prop.first) && prop.first && !prop.first.startsWith('_')) {
                  newProperties[prop.first] = prop.last;
                }
              });
            }
            
            mergedQuantity = existingItem.quantity + mergedQuantity;
            
            const changeUrl = (typeof routes !== 'undefined' && routes.cart_change_url) || (window.routes?.cart_change_url) || '/cart/change.js';
            const fetchConfigFn = typeof fetchConfig !== 'undefined' ? fetchConfig : () => ({
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              }
            });
            
            const changeConfig = fetchConfigFn();
            const itemIndex = cartData.items.findIndex(item => item.variant_id === parseInt(variantId));
            const lineNumber = itemIndex >= 0 ? (itemIndex + 1) : null;
            
            if (lineNumber) {
              const removeResponse = await fetch(changeUrl, {
                ...changeConfig,
                body: JSON.stringify({
                  line: lineNumber,
                  quantity: 0
                })
              });
              
              if (!removeResponse.ok) {
                const errorText = await removeResponse.text().catch(() => 'Unknown error');
                console.warn('Failed to remove existing cart item:', errorText);
              }
            }
            
            if (!removeResponse.ok) {
              const errorText = await removeResponse.text().catch(() => 'Unknown error');
              console.warn('Failed to remove existing cart item:', errorText);
            }
          }
        } catch (error) {
          console.warn('Could not check existing cart items:', error);
        }
        
        const mergedFormData = new FormData();
        mergedFormData.append('id', variantId);
        mergedFormData.append('quantity', mergedQuantity);
        
        Object.keys(newProperties).forEach(key => {
          mergedFormData.append(`properties[${key}]`, newProperties[key]);
        });
        
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[__shopify') && key.endsWith(']')) {
            mergedFormData.append(key, value);
          }
        }
        
        if (this.cart) {
          mergedFormData.append(
            "sections",
            this.cart
              .getSectionsToRender()
              .map(section => section.section)
          );
          mergedFormData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = mergedFormData;

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector(
                ".sold-out-message"
              );
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              const personalizationDrawer = document.querySelector('personalization-drawer');
              if (personalizationDrawer && personalizationDrawer.hasPersonalization()) {
                personalizationDrawer.clearPersonalizationData();
              }
              
              const giftPackagingDrawer = document.querySelector('gift-packaging-drawer');
              if (giftPackagingDrawer && giftPackagingDrawer.hasGiftPackaging()) {
                giftPackagingDrawer.clearGiftPackagingData();
              }
              
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response
              });
              
              const personalizationDrawer = document.querySelector('personalization-drawer');
              if (personalizationDrawer && personalizationDrawer.hasPersonalization()) {
                personalizationDrawer.clearPersonalizationData();
              }
              
              const giftPackagingDrawer = document.querySelector('gift-packaging-drawer');
              if (giftPackagingDrawer && giftPackagingDrawer.hasGiftPackaging()) {
                giftPackagingDrawer.clearGiftPackagingData();
              }
            }
            this.error = false;
            this.cart.renderContents(response);
          })
          .catch(e => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error)
              this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add(
              "hidden"
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute(
          "hidden",
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = this.hasAttribute(
            "data-has-selling-plan"
          )
            ? window.variantStrings.addSubscriptionToCart
            : this.submitButtonText.textContent = this.hasAttribute(
                "data-has-pre-order"
              )
            ?  window.variantStrings.preOrder
            : window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}

document.querySelectorAll('.product__content > *').forEach((el) => {
  if (!el.textContent.trim()) {
    el.textContent = '';
  }
});